import { useContext, useEffect, useRef } from "react";
import "ol/ol.css";
import { WhereToVoteOutlined } from "@mui/icons-material";
import { Feature, MapBrowserEvent } from "ol";
import { defaults } from "ol/control/defaults";
import WMTSCapabilities from "ol/format/WMTSCapabilities";
import { Geometry, Point } from "ol/geom";
import { Draw, Interaction, Modify } from "ol/interaction";
import { DrawEvent } from "ol/interaction/Draw";
import { ModifyEvent } from "ol/interaction/Modify";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import OlMap from "ol/Map";
import { Pixel } from "ol/pixel";
import { transform, useGeographic } from "ol/proj";
import { register } from "ol/proj/proj4";
import { Vector } from "ol/source";
import OSM from "ol/source/OSM";
import WMTS, { Options, optionsFromCapabilities } from "ol/source/WMTS";
import { Icon, Style } from "ol/style";
import View from "ol/View";
import proj4 from "proj4";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import pinIcon from "~/src/assets/location_on.svg";
import AlertDefault, { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import ButtonDefault, { ButtonVariant } from "~/src/components/Button/ButtonDefault";
import BoldText from "~/src/components/Text/BoldText";
import { TextVariant } from "~/src/components/Text/Text";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import { useStateful } from "~/src/hooks/useStateful";
import proj4Defs from "./proj4-defs.json";

export type Coordinates = [number, number];
type ProjDefPair = [string, string];

function Kart() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const ui = useContext(UIContext);
  const coordinatesState = useStateful<Coordinates | undefined>(undefined);
  const userPosition = useStateful<Coordinates | undefined>(undefined);
  const nord: number = Number(location.state?.nord?.toString().replace(",", "."));
  const ost: number = Number(location.state?.ost?.toString().replace(",", "."));
  const koordinatfesting: string = location.state?.koordinatfesting;
  const tilbakeTilForrigeSide: string = location.state?.referrer;
  const zoomValgtPosisjon: number = 14;
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OlMap | null>(null);
  const drawRef = useRef<Draw | null>(null);
  const modifyRef = useRef<Modify | null>(null);
  const vectorRef = useRef<VectorLayer<Feature<Geometry>> | null>(null);

  proj4.defs(proj4Defs.defs as ProjDefPair[]);
  register(proj4);
  useGeographic();

  const overrideZoomButtonCss = `
    .ol-zoom {
      width: fit-content;
      position: relative;
      top: calc(100vh - 20rem);
      left: calc(100vw - 4.5rem);
      background-color: transparent;
    }
      
    @supports (height: 100dvh) {
      .ol-zoom {
        top: calc(100dvh - 20rem);
        left: calc(100dvw - 4.5rem);
      }
    }

    @media only screen and (min-width: 946px) {
      .ol-zoom {
        left: 900px;
      }
    }
    .ol-zoom button.ol-zoom-out {
      align-items: baseline;

      @media (hover: none) { /* Touch devices */
        align-items: center;
      }
    }
    .ol-zoom button {
      background-color: #006178;
      opacity: 85%;
      border: none;
      color: white;
      font-weight: 400 !important;
      font-family: 'Arial', 'Helvetica', sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      font-size: 3.5rem !important;
      min-width: 3.5rem;
      height: 3.5rem;
      width: auto;
      display: flex;
      justify-content: center;
      align-items: center !important;
      text-alignment: center;
      transition: background-color 0.1s ease, color 0.1s ease;
    }
    .ol-zoom button:focus-visible {
      background-color: white;
      color: #006178;
      outline: none;
    }
    .ol-zoom button:hover { 
      color: white;
      outline: none;
    }
    .ol-zoom button:active { 
      color: #01323D;
      outline: none;
      background-color: #81C5D5;
    }
    .ol-zoom .ol-zoom-in{
      border-top-right-radius: 100%;
      border-top-left-radius: 100%;
      padding-top: 0.5rem;
    }
    .ol-zoom .ol-zoom-out {
      border-bottom-right-radius: 100%;
      border-bottom-left-radius: 100%;
      padding-bottom: 0.5rem;
    }
    .nonTransparent, .nonTransparent:focus{
      opacity: 100% !important
    }
  `;

  useEffect(() => {
    if (location.state?.nord && location.state?.ost && location.state?.koordinatfesting) {
      if (koordinatfesting.startsWith("UTM")) {
        const coords = transform(
          [+ost, +nord],
          epsgKode(koordinatfesting),
          epsgKode("LengdeOgBreddegrad"), //Fordi kartet er registrert til å kunne vise koordinater i lengde og breddegrad pga useGeographic()
        );
        coordinatesState.set([coords[0], coords[1]]);
        mapRef.current
          ?.getView()
          .animate({ zoom: zoomValgtPosisjon, center: [coords[0], coords[1]] });
      } else {
        coordinatesState.set([ost, nord]);
        mapRef.current?.getView().animate({ zoom: zoomValgtPosisjon, center: [ost, nord] });
      }
    }
    function epsgKode(system: string): string {
      switch (system) {
        case "UTM32":
          return "EPSG:32632";
        case "UTM33":
          return "EPSG:32633";
        case "UTM34":
          return "EPSG:32634";
        case "UTM35":
          return "EPSG:32635";
        case "UTM36":
          return "EPSG:32636";
        case "LengdeOgBreddegrad":
        default:
          return "EPSG:4326";
      }
    }
  }, []);

  useEffect(() => {
    //Kalles kun en gang
    if (ref.current && !mapRef.current) {
      const source = new Vector();
      vectorRef.current = new VectorLayer({
        source: source,
        zIndex: 100,
        style: new Style({
          image: new Icon({
            opacity: 1,
            crossOrigin: "*",
            src: pinIcon,
            width: 48,
            height: 48,
            anchor: [0.5, 1],
            anchorYUnits: "fraction",
            anchorXUnits: "fraction",
          }),
        }),
      });

      const parser = new WMTSCapabilities();
      const norgesKart = new TileLayer();

      fetch("https://cache.kartverket.no/v1/service?service=WMTS&request=GetCapabilities")
        .then(function (response) {
          return response.text();
        })
        .then(function (text) {
          const result = parser.read(text);
          const options = optionsFromCapabilities(result, {
            layer: "topo",
            projection: "EPSG:3857",
          });
          norgesKart.setOpacity(1);
          norgesKart.setSource(new WMTS(options as Options));
        });

      mapRef.current = new OlMap({
        target: ref.current,
        layers: [
          new TileLayer({
            preload: Infinity,
            source: new OSM(),
          }),
          norgesKart,
          vectorRef.current,
        ],
        view: new View({
          center: [18, 63],
          zoom: 4,
          maxZoom: 17,
          enableRotation: false,
        }),
        controls: defaults({
          rotate: false,
          attribution: false,
          zoom: true,
        }),
      });

      drawRef.current = new Draw({
        source: source,
        type: "Point",
        minPoints: 0,
        maxPoints: 1,
      });

      modifyRef.current = new Modify({
        source: source,
        hitDetection: vectorRef.current,
      });

      drawRef.current?.on("drawend", (e: DrawEvent) => {
        mapRef.current?.removeInteraction(drawRef.current as Interaction);
        mapRef.current?.addInteraction(modifyRef.current as Interaction);

        const point = e.feature.getGeometry() as Point;
        let [x, y] = point.getCoordinates();
        coordinatesState.set([x, y]);
      });

      modifyRef.current.on("modifyend", (e: ModifyEvent) => {
        const point = e.features.item(0).getGeometry() as Point;
        let [x, y] = point.getCoordinates();
        coordinatesState.set([x, y]);
      });

      //Setter cursor til 'move' type stil om man hovrer over flyttbar markør (kun synlig i nettleser)
      const map = mapRef.current;
      if (map) {
        map?.on("pointermove", (e: MapBrowserEvent<PointerEvent>) => {
          const pixel: Pixel = e.pixel;
          const hit = map.hasFeatureAtPixel(pixel);
          map.getViewport().style.cursor = hit ? "move" : "";
        });
      }

      settRiktigInteraksjon();
    }
  }, [ref, mapRef]);

  function oppdaterPosisjonIKart(coordinates: Coordinates) {
    const feature = vectorRef.current?.getSource()?.getFeatures()[0];

    if (feature) {
      (feature.getGeometry() as Point).setCoordinates(coordinates);
      feature.changed();
    } else {
      const point = new Feature(new Point(coordinates));
      vectorRef.current?.getSource()?.addFeature(point);
    }

    var currentZoom = mapRef.current?.getView().getZoom();
    if (currentZoom !== undefined && currentZoom < zoomValgtPosisjon) {
      mapRef.current?.getView().animate({ zoom: zoomValgtPosisjon, center: coordinates });
    } else {
      mapRef.current?.getView().animate({ center: coordinates });
    }

    settRiktigInteraksjon();
  }

  function settRiktigInteraksjon() {
    //Nullstiller alt først
    mapRef.current?.removeInteraction(drawRef.current as Interaction);
    mapRef.current?.removeInteraction(modifyRef.current as Interaction);

    if (coordinatesState.value) {
      mapRef.current?.addInteraction(modifyRef.current as Interaction);
    } else {
      mapRef.current?.addInteraction(drawRef.current as Interaction);
    }
  }

  useEffect(() => {
    if (coordinatesState.value) {
      oppdaterPosisjonIKart(coordinatesState.value);
    }
  }, [coordinatesState.value]);

  return (
    <>
      <div className="relative">
        <div className="absolute z-40 top-0 left-0 w-screen max-w-244 flex flex-col items-end">
          <div className="m-4 mt-2">
            <AlertDefault
              canClose
              rounded
              className="rounded-2xl shadow-lg"
              variant={AlertVariant.Info}
            >
              <BoldText
                variant={TextVariant.small}
                text={t("pages.kart.beskrivelse")}
                specificWords={[3, 4]}
              />
            </AlertDefault>
          </div>
        </div>
      </div>
      <div ref={ref} style={{ width: "100%", height: "100%" }}></div>

      <div
        className={
          "fixed bottom-0 left-0 right-0 mb-5 z-50 w-full justify-center flex items-center gap-2 md:gap-8"
        }
      >
        <ButtonDefault
          className="max-w-100 ml-2 bg-ldir-blue-900 border-ldir-blue-900 nonTransparent"
          lessPadding
          active={
            userPosition.value != undefined &&
            coordinatesState.value != undefined &&
            userPosition.value[0] == coordinatesState.value[0] &&
            userPosition.value[1] == coordinatesState.value[1]
          }
          label={t("pages.kart.minPosisjon")}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              role="graphics-symbol"
            >
              <path
                d="M10.9041 24V21.8082C8.621 21.5525 6.6621 20.6073 5.0274 18.9726C3.39269 17.3379 2.44749 15.379 2.19178 13.0959H0V10.9041H2.19178C2.44749 8.621 3.39269 6.6621 5.0274 5.0274C6.6621 3.39269 8.621 2.44749 10.9041 2.19178V0H13.0959V2.19178C15.379 2.44749 17.3379 3.39269 18.9726 5.0274C20.6073 6.6621 21.5525 8.621 21.8082 10.9041H24V13.0959H21.8082C21.5525 15.379 20.6073 17.3379 18.9726 18.9726C17.3379 20.6073 15.379 21.5525 13.0959 21.8082V24H10.9041ZM12 19.6712C14.1187 19.6712 15.9269 18.9224 17.4247 17.4247C18.9224 15.9269 19.6712 14.1187 19.6712 12C19.6712 9.88128 18.9224 8.07306 17.4247 6.57534C15.9269 5.07763 14.1187 4.32877 12 4.32877C9.88128 4.32877 8.07306 5.07763 6.57534 6.57534C5.07763 8.07306 4.32877 9.88128 4.32877 12C4.32877 14.1187 5.07763 15.9269 6.57534 17.4247C8.07306 18.9224 9.88128 19.6712 12 19.6712ZM12 16.3836C10.7945 16.3836 9.76256 15.9543 8.90411 15.0959C8.04566 14.2374 7.61644 13.2055 7.61644 12C7.61644 10.7945 8.04566 9.76256 8.90411 8.90411C9.76256 8.04566 10.7945 7.61644 12 7.61644C13.2055 7.61644 14.2374 8.04566 15.0959 8.90411C15.9543 9.76256 16.3836 10.7945 16.3836 12C16.3836 13.2055 15.9543 14.2374 15.0959 15.0959C14.2374 15.9543 13.2055 16.3836 12 16.3836ZM12 14.1918C12.6027 14.1918 13.1187 13.9772 13.5479 13.5479C13.9772 13.1187 14.1918 12.6027 14.1918 12C14.1918 11.3973 13.9772 10.8813 13.5479 10.4521C13.1187 10.0228 12.6027 9.80822 12 9.80822C11.3973 9.80822 10.8813 10.0228 10.4521 10.4521C10.0228 10.8813 9.80822 11.3973 9.80822 12C9.80822 12.6027 10.0228 13.1187 10.4521 13.5479C10.8813 13.9772 11.3973 14.1918 12 14.1918Z"
                fill="currentColor"
              />
            </svg>
          }
          id="minPosisjonKartButton"
          type="button"
          disabled={navigator.geolocation === undefined}
          aria-disabled={navigator.geolocation === undefined}
          title={t("pages.kart.minPosisjon")}
          aria-label={t("pages.kart.minPosisjon") + " knapp"}
          variant={ButtonVariant.Primary}
          onClick={async () => {
            function success(position: GeolocationPosition) {
              coordinatesState.set([position.coords.longitude, position.coords.latitude]);
              userPosition.set([position.coords.longitude, position.coords.latitude]);
              mapRef.current?.getView().animate({
                zoom: zoomValgtPosisjon,
                center: [position.coords.longitude, position.coords.latitude],
              });
            }

            function error(error: GeolocationPositionError) {
              if (error.code == error.PERMISSION_DENIED) {
                ui.showSnackbar({
                  message: t("pages.kart.geolocationPositionDeniedError"),
                  severity: AlertVariant.Warning,
                });
              } else {
                ui.showSnackbar({
                  message: t("pages.kart.geolocationPositionUnavailableError"),
                  severity: AlertVariant.Warning,
                });
              }
            }

            const options = {
              enableHighAccuracy: true,
              timeout: 3000,
            };

            navigator.geolocation.getCurrentPosition(success, error, options);
          }}
        />

        <ButtonDefault
          className="max-w-100 mr-2 bg-ldir-blue-900 border-ldir-blue-900 disabled:bg-[#007B97] disabled:border-[#007B97] disabled:text-[#E3EDF0BF] disabled:opacity-100 nonTransparent"
          lessPadding
          id="bekreftValgKartButton"
          label={t("pages.kart.bekreftValg")}
          variant={ButtonVariant.Primary}
          icon={<WhereToVoteOutlined />}
          disabled={!coordinatesState.value}
          aria-disabled={!coordinatesState.value}
          onClick={async () => {
            if (coordinatesState.value) {
              navigate(tilbakeTilForrigeSide, {
                state: {
                  nord: coordinatesState.value[1].toFixed(6).toString(),
                  ost: coordinatesState.value[0].toFixed(6).toString(),
                  koordinatfesting: "LengdeOgBreddegrad",
                },
              });
            }
          }}
        />
      </div>
      <style>{overrideZoomButtonCss}</style>
    </>
  );
}

export default Kart;
